import { useMemo } from "react"
import { WHATS_NEW_LINK } from "@/v2-common/featureSurface/featureSurface.constants"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import Table, { type TableProps } from "@/v2-backoffice-client/components/table"
import LicenseChangeRowToolbar from "@/v2-backoffice-client/license/changes/LicenseChangeRowToolbar"
import LicenseChangesAbout from "@/v2-backoffice-client/license/changes/LicenseChangesAbout"
import { CAPABILITY_SHEET_KIND } from "@/v2-backoffice-client/license/changes/license.changes.constants"
import { LicenseChangesContext } from "@/v2-backoffice-client/license/changes/license.changes.context"
import { useFeatureSurfaceChangesQuery } from "@/v2-backoffice-client/license/changes/license.changes.query"
import { createChangeColumns } from "@/v2-backoffice-client/license/changes/license.changes.columns"
import {
  DECISION_STATUS,
  statusOf,
  type LicenseChange,
  type LicenseChangeStatus,
} from "@/v2-backoffice-client/license/changes/license.changes.utils"
import { useFeatureGroupsQuery } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.query"

export type LicenseChangesTableProps = Omit<
  TableProps<LicenseChange>,
  "data" | "columns"
>

// The Capabilities view of Feature access: every capability a release has
// added to Fusion, and who gets it. Until someone decides, a new capability
// is free for every customer. Each row is one capability, in the words the
// app uses for it, with what has been decided and which packages carry it;
// the row opens the capability's panel, where the decision is taken with
// the lock's blast radius in view, and the row's menu offers the one
// decision that needs no more than a click. The board owns the two reads
// (the feed, shared through the Apollo cache with the tab trigger's count
// and the panel, and the package vocabulary the Packages pills link by).
function LicenseChangesTable(props: LicenseChangesTableProps) {
  const { className, ...tableProps } = props
  const opener = useAppViewSheetOpener()

  const { data, previousData, loading, error, refetch } =
    useFeatureSurfaceChangesQuery()
  const { data: packagesData } = useFeatureGroupsQuery()
  // previousData keeps rows rendered through post-save evictions.
  const changes = (data ?? previousData)?.getFeatureSurfaceChanges ?? []
  const packages = packagesData?.getFeatureGroups ?? []

  const counts = useMemo(() => {
    const counted: Record<LicenseChangeStatus, number> = {
      [DECISION_STATUS.needsDecision]: 0,
      [DECISION_STATUS.everyone]: 0,
      [DECISION_STATUS.packages]: 0,
      [DECISION_STATUS.removed]: 0,
    }
    for (const change of changes) counted[statusOf(change)] += 1
    return counted
  }, [changes])

  const columns = useMemo(() => createChangeColumns(), [])

  return (
    <LicenseChangesContext value={{ packages }}>
      <div className="flex h-full min-h-0 flex-col gap-2">
        <LicenseChangesAbout />
        <Table
          className={cn("license-changes-table min-h-0 flex-1", className)}
          tableId="license-feature-access-capabilities"
          data={changes}
          columns={columns}
          enableGrouping
          getRowId={(row) => String(row.Id)}
          onRowClick={(row) => {
            opener.open(CAPABILITY_SHEET_KIND, row.original.SurfaceCode)
          }}
          getHighlightedRowId={(row) =>
            opener.getIsActive(CAPABILITY_SHEET_KIND, row.original.SurfaceCode)
          }
          {...tableProps}
        >
          <Table.Toolbar>
            <Table.StatusFilter
              column="Status"
              urlParam={WHATS_NEW_LINK.statusParam}
              defaultValue={0}
              isLoading={loading}
            >
              <Table.StatusFilter.Option
                value={0}
                filterValue={DECISION_STATUS.needsDecision}
                variant="warning"
                iconSrc="exclamation"
                count={counts[DECISION_STATUS.needsDecision]}
              >
                Needs a decision
              </Table.StatusFilter.Option>
              <Table.StatusFilter.Option
                value={1}
                filterValue={DECISION_STATUS.everyone}
                variant="success"
                iconSrc="unlock"
                count={counts[DECISION_STATUS.everyone]}
              >
                Free for everyone
              </Table.StatusFilter.Option>
              <Table.StatusFilter.Option
                value={2}
                filterValue={DECISION_STATUS.packages}
                iconSrc="lock"
                count={counts[DECISION_STATUS.packages]}
              >
                Package-only
              </Table.StatusFilter.Option>
              <Table.StatusFilter.Option
                value={3}
                filterValue={DECISION_STATUS.removed}
                iconSrc="trash"
                count={counts[DECISION_STATUS.removed]}
              >
                Removed
              </Table.StatusFilter.Option>
              <Table.StatusFilter.Option
                value={null}
                filterValue={WHATS_NEW_LINK.statusAll}
                iconSrc="list"
                count={changes.length}
              >
                All
              </Table.StatusFilter.Option>
            </Table.StatusFilter>

            <div className="flex-1" />
            {/* Deep-linkable (the console's dev toolbar opens a capability
                here by code), so the keys are the shared WHATS_NEW_LINK. */}
            <Table.Search
              column="Feature"
              urlParam={WHATS_NEW_LINK.searchParam}
              placeholder="Search by capability..."
            />

            <Table.ToolbarFilters />
            <Separator orientation="vertical" />

            <Table.ToolbarMenu
              onRefresh={() => void refetch()}
              exportFileName="feature-access-capabilities"
            />
          </Table.Toolbar>

          <Table.FilterList />
          <Table.GroupList />

          <Table.RowToolbar<LicenseChange> column="Feature">
            {(change) => <LicenseChangeRowToolbar change={change} />}
          </Table.RowToolbar>

          <Table.Content isLoading={loading} error={error} />
        </Table>
      </div>
    </LicenseChangesContext>
  )
}

export default LicenseChangesTable
